/**
 * Builds the `srcdoc` for tenant-supplied embed HTML (Content block "Embed HTML").
 *
 * The document runs in a sandboxed iframe WITHOUT `allow-same-origin`, so the
 * parent cannot read its height. A small reporter script is injected that
 * posts the content height to the parent instead.
 *
 * Keep in sync with fe-client-swiift-studio `src/utils/embedSrcDoc.ts`.
 */

export const EMBED_HEIGHT_MESSAGE = "swiift:embed-height";

/** Stops `100vh` content from growing the frame forever (each resize feeds the next). */
export const EMBED_MAX_HEIGHT = 20000;

const REPORTER_SCRIPT = `<script>(function () {
  var last = -1;
  function report() {
    var d = document.documentElement;
    var b = document.body;
    var h = Math.ceil(Math.max(
      d ? d.getBoundingClientRect().height : 0,
      b ? b.scrollHeight : 0
    ));
    if (h === last) return;
    last = h;
    parent.postMessage({ type: "${EMBED_HEIGHT_MESSAGE}", height: h }, "*");
  }
  function start() {
    report();
    if (typeof ResizeObserver !== "undefined") {
      var ro = new ResizeObserver(report);
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
    }
    // Embed SDKs often render late (iframes inside iframes); poll briefly.
    var poll = setInterval(report, 500);
    setTimeout(function () { clearInterval(poll); }, 10000);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  window.addEventListener("load", report);
})();</script>`;

const FULL_DOCUMENT_RE = /<!doctype\s|<html[\s>]/i;

/**
 * Links default to a new tab: without it a click navigates the small iframe
 * itself, and most sites refuse to be framed anyway.
 */
const headInjection = (html: string) =>
  (/<base[\s>]/i.test(html) ? "" : '<base target="_blank">') + REPORTER_SCRIPT;

export const buildEmbedSrcDoc = (html: string): string => {
  if (!FULL_DOCUMENT_RE.test(html)) {
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html, body { margin: 0; padding: 0; } body { overflow-x: hidden; }</style>
${headInjection(html)}
</head>
<body>${html}</body>
</html>`;
  }

  const injection = headInjection(html);
  const headOpen = html.match(/<head(\s[^>]*)?>/i);
  if (headOpen && headOpen.index !== undefined) {
    const at = headOpen.index + headOpen[0].length;
    return html.slice(0, at) + injection + html.slice(at);
  }
  const htmlOpen = html.match(/<html(\s[^>]*)?>/i);
  if (htmlOpen && htmlOpen.index !== undefined) {
    const at = htmlOpen.index + htmlOpen[0].length;
    return html.slice(0, at) + `<head>${injection}</head>` + html.slice(at);
  }
  // `<!doctype html>` followed directly by body content. Anything before the
  // doctype would drop the document into quirks mode.
  const doctype = html.match(/<!doctype[^>]*>/i);
  if (doctype && doctype.index !== undefined) {
    const at = doctype.index + doctype[0].length;
    return html.slice(0, at) + injection + html.slice(at);
  }
  return injection + html;
};
